/**
 * Закрытые словари рабочего движка — один набор на всё приложение.
 *
 * Существует ради одной ошибки, которую легко сделать в разметке: напечатать
 * код словаря как есть. «категория tillage», «статус: active», «точность: day»
 * — это не подписи, а внутренние коды сервера, и человек, читающий экран,
 * обязан видеть слова, а не перечисление из `work/enums.py`.
 *
 * Правило то же, что у культур (`crops.ts`), эпистемики и степени выполнения:
 * незнакомый код НЕ подменяется похожим знакомым и не прячется — он
 * показывается как «неизвестно: <код>», и `known: false` позволяет разметке
 * пометить его отдельно. Новое значение словаря на сервере обязано быть видно
 * на экране, а не тихо стать одним из старых.
 *
 * Место — `lib/`, а не отдельный экран: те же коды печатают уровень работы
 * (`features/works`) и карточка кампании (`features/campaigns`), и второй
 * список подписей однажды разошёлся бы с первым.
 *
 * Источник значений — `backend/work/enums.py`. Словари хранят описания
 * сообщений каталога; строку делает `tr` при выдаче, чтобы смена языка
 * подхватывалась.
 */

import { defineMessages, type Message } from "../i18n/define";
import { tr } from "../i18n/runtime";
import { lookup } from "./crops";

export interface VocabView {
  label: string;
  /** Код нашёлся в словаре. `false` — сервер прислал значение, которого приложение не знает. */
  known: boolean;
}

const EMPTY = defineMessages({
  campaignStatus: { id: "common.vocab.campaignStatus.empty", defaultMessage: "статус не назван", description: "Подпись, когда у кампании не пришёл статус" },
  workCategory: { id: "common.vocab.workCategory.empty", defaultMessage: "категория не названа", description: "Подпись, когда у работы не пришла категория" },
  deadlineKind: { id: "common.vocab.deadlineKind.empty", defaultMessage: "форма срока не названа", description: "Подпись, когда у работы не пришла форма срока" },
  datePrecision: { id: "common.vocab.datePrecision.empty", defaultMessage: "точность не названа", description: "Подпись, когда не пришла точность даты факта" },
  obligationState: { id: "common.vocab.obligationState.empty", defaultMessage: "состояние не названо", description: "Подпись, когда не пришло состояние работы на поле или хозяйства" },
});

function view(
  labels: Record<string, Message>,
  code: string | null | undefined,
  empty: Message,
): VocabView {
  // Пустой код — своя подпись («статус не назван»), а не общее «неизвестно».
  if (!code) return { label: tr(empty as never), known: false };
  return lookup(labels, code);
}

/** `work.enums.CampaignStatus`. */
const CAMPAIGN_STATUS_LABELS: Record<string, Message> = defineMessages({
  planned: { id: "common.vocab.campaignStatus.planned", defaultMessage: "запланирована", description: "Статус кампании: запланирована (строчная, в строке или бейдже)" },
  announced: { id: "common.vocab.campaignStatus.announced", defaultMessage: "объявлена", description: "Статус кампании: объявлена фермерам" },
  active: { id: "common.vocab.campaignStatus.active", defaultMessage: "идёт", description: "Статус кампании: идёт сейчас" },
  paused: { id: "common.vocab.campaignStatus.paused", defaultMessage: "приостановлена", description: "Статус кампании: приостановлена" },
  completed: { id: "common.vocab.campaignStatus.completed", defaultMessage: "завершена", description: "Статус кампании: завершена" },
  cancelled: { id: "common.vocab.campaignStatus.cancelled", defaultMessage: "отменена", description: "Статус кампании: отменена" },
});

export function campaignStatusLabel(code: string | null | undefined): VocabView {
  return view(CAMPAIGN_STATUS_LABELS, code, EMPTY.campaignStatus);
}

/** `work.enums.WorkCategory`. */
const WORK_CATEGORY_LABELS: Record<string, Message> = defineMessages({
  tillage: { id: "common.vocab.workCategory.tillage", defaultMessage: "вспашка", description: "Вид работы: вспашка (строчная)" },
  sowing: { id: "common.vocab.workCategory.sowing", defaultMessage: "посев", description: "Вид работы: посев (строчная)" },
  irrigation: { id: "common.vocab.workCategory.irrigation", defaultMessage: "полив", description: "Вид работы: полив (строчная)" },
  treatment: { id: "common.vocab.workCategory.treatment", defaultMessage: "обработка", description: "Вид работы: обработка посевов (строчная)" },
  harvest: { id: "common.vocab.workCategory.harvest", defaultMessage: "сбор", description: "Вид работы: сбор урожая (строчная)" },
  q_inspection: { id: "common.vocab.workCategory.q_inspection", defaultMessage: "карантинное обследование", description: "Вид работы: обследование на карантинные объекты инспектором" },
  quarantine_measure: { id: "common.vocab.workCategory.quarantine_measure", defaultMessage: "карантинные меры", description: "Вид работы: меры по предписанию карантина" },
  other: { id: "common.vocab.workCategory.other", defaultMessage: "прочее", description: "Вид работы: прочее (строчная)" },
});

export function workCategoryLabel(code: string | null | undefined): VocabView {
  return view(WORK_CATEGORY_LABELS, code, EMPTY.workCategory);
}

/**
 * `work.enums.DeadlineKind`. `none` — это «срока нет», а не «срок неизвестен»:
 * у такой работы просрочки не существует в принципе.
 */
const DEADLINE_KIND_LABELS: Record<string, Message> = defineMessages({
  fixed_date: { id: "common.vocab.deadlineKind.fixed_date", defaultMessage: "фиксированная дата", description: "Форма срока работы: одна дата" },
  window: { id: "common.vocab.deadlineKind.window", defaultMessage: "окно", description: "Форма срока работы: окно «с — по»" },
  periodic: { id: "common.vocab.deadlineKind.periodic", defaultMessage: "периодичность", description: "Форма срока работы: повторяется регулярно" },
  offset: { id: "common.vocab.deadlineKind.offset", defaultMessage: "смещение от события", description: "Форма срока работы: через N дней после другого события" },
  none: { id: "common.vocab.deadlineKind.none", defaultMessage: "без срока", description: "Форма срока работы: срока нет, просрочки не бывает" },
});

export function deadlineKindLabel(code: string | null | undefined): VocabView {
  return view(DEADLINE_KIND_LABELS, code, EMPTY.deadlineKind);
}

/**
 * `work.enums.DatePrecision`. Точность — свойство ЗНАНИЯ, а не оформления:
 * «выполнено в апреле» и «выполнено 12 апреля» — разные утверждения.
 */
const DATE_PRECISION_LABELS: Record<string, Message> = defineMessages({
  day: { id: "common.vocab.datePrecision.day", defaultMessage: "день", description: "Точность даты факта: известен день" },
  month: { id: "common.vocab.datePrecision.month", defaultMessage: "месяц", description: "Точность даты факта: известен только месяц" },
  interval: { id: "common.vocab.datePrecision.interval", defaultMessage: "интервал", description: "Точность даты факта: известен только интервал дат" },
});

export function datePrecisionLabel(code: string | null | undefined): VocabView {
  return view(DATE_PRECISION_LABELS, code, EMPTY.datePrecision);
}

/**
 * Состояние работы на поле и состояние работы ХОЗЯЙСТВА — два словаря,
 * а не один, и различие принадлежит контракту, а не экрану.
 *
 * `ObligationState` — про один контур: отчёт по нему либо есть, либо нет, и
 * «части» у него не бывает. `FarmObligationState` — про хозяйство целиком, и
 * у него есть шестое значение `partial`: отчитались не все контуры. Свести их
 * в один словарь значило бы либо предлагать контуру невозможное состояние,
 * либо потерять у хозяйства то, ради которого свёртка и заведена.
 *
 * Живут здесь, а не по копии на экран: карточка кампании печатала свой набор
 * кодов, экран работ — свой, и два списка одних и тех же подписей однажды
 * разошлись бы молча. Полнота обоих сверяется с `openapi.json`
 * (`workVocab.test.ts`), поэтому новое значение сервера роняет тест на
 * следующей же перегенерации типов.
 */
const OBLIGATION_STATE_LABELS: Record<string, Message> = defineMessages({
  no_data: { id: "common.vocab.obligationState.no_data", defaultMessage: "Данных нет", description: "Состояние работы на поле: от фермера и инспектора ничего не пришло" },
  reported: { id: "common.vocab.obligationState.reported", defaultMessage: "Отчитались", description: "Состояние работы на поле: отчёт есть, замера инспектора нет" },
  confirmed: { id: "common.vocab.obligationState.confirmed", defaultMessage: "Подтверждено", description: "Состояние работы на поле: факт подтверждён" },
  needs_clarification: { id: "common.vocab.obligationState.needs_clarification", defaultMessage: "Требует уточнения", description: "Состояние работы на поле: нужно уточнить данные у фермера" },
  conflict: { id: "common.vocab.obligationState.conflict", defaultMessage: "Конфликт", description: "Состояние работы на поле: источники фактов расходятся" },
});

const FARM_OBLIGATION_STATE_LABELS: Record<string, Message> = {
  ...OBLIGATION_STATE_LABELS,
  ...defineMessages({
    partial: { id: "common.vocab.obligationState.partial", defaultMessage: "Часть контуров", description: "Состояние работы хозяйства: отчитались не все его контуры" },
  }),
};

/** `work.enums.ObligationState` — состояние работы на ОДНОМ поле (контуре). */
export function obligationStateLabel(code: string | null | undefined): VocabView {
  return view(OBLIGATION_STATE_LABELS, code, EMPTY.obligationState);
}

/** `work.enums.FarmObligationState` — свёртка по всем контурам хозяйства. */
export function farmObligationStateLabel(
  code: string | null | undefined,
): VocabView {
  return view(FARM_OBLIGATION_STATE_LABELS, code, EMPTY.obligationState);
}
